import MapStore from 'app/src/editor/MapStore';
import { MapStoreContextProvider } from 'app/src/editor/MapStoreContext';
import { usePluginsContext } from 'app/src/editor/PluginsContext';
import useEditorLocalStorage from 'app/src/editor/useEditorLocalStorage';
import useForceUpdate from 'app/src/utils/useForceUpdate';
import useInitializer from 'app/src/utils/useInitializer';
import * as React from 'react';

type Props = {
	children: React.ReactNode;
};

export default function MapStoreController({ children }: Props): React.ReactElement | null {
	const plugins = usePluginsContext();

	const { getEditorLevelDescription, saveEditorLevelDescription } = useEditorLocalStorage();

	// Build a new MapStore on first mount and keep it around until unmount.
	const mapStore = useInitializer(() => {
		const store = new MapStore(plugins);

		const localLevelDescription = getEditorLevelDescription();
		if (localLevelDescription) {
			store.setLevelDescription(localLevelDescription);
		}

		return store;
	}, []);

	// This is pretty hacky, but it works. Whenever we want to force an update of
	// this component, consumers can call forceUpdate(). This is necessary because
	// MapStore is a vanilla class and we don't have any bindings to its data.
	const forceUpdate = useForceUpdate();

	const saveLocalData = React.useCallback(
		() => saveEditorLevelDescription(mapStore.buildLevelDescription()),
		[mapStore, saveEditorLevelDescription],
	);

	// All updates to mapStore should trigger the instance of PubSub to trigger a
	// re-render of this component.
	React.useEffect(() => {
		mapStore.onUpdate.subscribe(forceUpdate);
		mapStore.onUpdate.subscribe(saveLocalData);
		return (): void => {
			mapStore.onUpdate.unsubscribe(forceUpdate);
			mapStore.onUpdate.unsubscribe(saveLocalData);
		};
	});

	return <MapStoreContextProvider value={{ mapStore }}>{children}</MapStoreContextProvider>;
}
